import React, { useContext } from "react";
import { motion } from "framer-motion";
import { TypeAnimation } from 'react-type-animation';
import Social from "./Hero/Social";
import Photo from "./Hero/Photo";
import { HeaderContext } from "../context/HeaderContext";

const Hero = () => {
  const { heroes, imageUrl } = useContext(HeaderContext);

  return (
    <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">

      {/* Animated Background Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(40)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white/5"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              width: Math.random() * 15 + 5,
              height: Math.random() * 15 + 5,
            }}
            animate={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight,
              transition: {
                duration: Math.random() * 20 + 20,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "linear",
              },
            }}
          />
        ))}
      </div>

      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 opacity-10 [background-image:linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] [background-size:40px_40px]"></div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-green-900/10 to-blue-900/10 pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10 py-8 md:py-32">
        {/* Mobile: Photo on top, Content below */}
        <div className="block lg:hidden mt-4 mx-2">
          {/* Mobile Photo */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
            className="relative mb-10"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-green-500 rounded-2xl opacity-20 blur-xl"></div>
              <div className="relative overflow-hidden rounded-xl shadow-2xl border-2 border-white/10">
                <Photo onPhoto={`${imageUrl}/${heroes.image}`} />
              </div>
              <motion.div 
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.2 }}
                className="absolute -bottom-6 -left-6 bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-xl shadow-xl"
              >
                <div className="text-center">
                  <div className="text-3xl font-bold">{heroes.happy_customers}</div>
                  <div className="text-sm">Happy Students</div>
                </div>
              </motion.div>
              
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -top-6 -right-6 bg-white/5 backdrop-blur-sm p-3 rounded-full border border-white/10 shadow-lg"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Mobile Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            {/* <motion.p 
              className="text-lg font-medium text-green-400 mb-4"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.2 }}
            >
              <span className="p-2 inline-block bg-green-500/20 px-3 py-1 rounded-full">
                {heroes.headline}
              </span>
            </motion.p> */}
            
            <motion.h1 
              className="text-2xl sm:text-4xl font-bold leading-tight mb-6"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.4 }}
            >
              {heroes.headline}
              {/* সঠিক <span className="text-green-400">ফানেল</span> থাকলে {' '}
              <TypeAnimation
                sequence={[
                  'বিক্রি বাড়াবে', 1000,
                  'সময় বাঁচাবে', 1000,
                ]}
                wrapper="span"
                cursor={true}
                repeat={Infinity}
                className="block text-green-400 mt-2"
                style={{ display: 'inline-block' }}
              /> */}
            </motion.h1>

            <motion.p 
              className="text-base text-gray-300 mb-8 mx-auto max-w-md"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.6 }}
            >
              {heroes.desc}
            </motion.p>
            
            <motion.div
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.8 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-3 px-6 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl group"
              >
                <a href={heroes.button_link} className="relative z-10 text-white no-underline">{heroes.button_text}</a>
                <span className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </motion.button>
              
              {/* <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative border-2 border-white text-white font-bold py-3 px-6 rounded-full transition-all duration-300 group overflow-hidden"
              >
                <span className="relative z-10">Free Consultation</span>
                <span className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </motion.button> */}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-8 flex justify-center"
            >
              <Social />
            </motion.div>
          </motion.div>
        </div>

        {/* Desktop: Original Layout (Content left, Photo right) */}
        <div className="hidden lg:flex flex-row items-center gap-12">
          {/* Desktop Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:w-1/2 lg:text-left"
          >
            {/* <motion.p 
              className="text-xl font-medium text-green-400 mb-4"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.2 }}
            >
              <span className="inline-block p-2 bg-green-500/20 px-3 py-1 rounded-full">
                {heroes.headline}
              </span>
            </motion.p> */}
            
            <motion.h1 
              className="text-2xl lg:text-4xl font-bold leading-tight mb-6"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.4 }}
            >

              {heroes.headline}

              {/* একটি <span className="text-green-400">সঠিক </span> ফানেল {' '}
              <TypeAnimation
                sequence={[
                  'বিক্রি বাড়াবে', 1000,
                  'সময় বাঁচাবে', 1000,
                ]}
                wrapper="span"
                cursor={true}
                repeat={Infinity}
                className="block text-green-400 mt-2"
                style={{ display: 'inline-block' }}
              /> */}
            </motion.h1>

            <motion.p 
              className="text-xl text-gray-300 mb-8 max-w-2xl"
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.6 }}
            >
              {heroes.desc}
            </motion.p>
            
            <motion.div
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ delay: 0.8 }}
              className="flex flex-row gap-4 justify-start"
            >
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative bg-gradient-to-r from-green-500 to-green-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl group"
              >
                <a href={heroes.button_link} className="relative z-10 text-white no-underline">{heroes.button_text}</a>
                <span className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </motion.button>
              
              {/* <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="relative border-2 border-white text-white font-bold py-4 px-8 rounded-full transition-all duration-300 group overflow-hidden"
              >
                <a href={heroes.button_link} className="relative z-10 text-white no-underline">{heroes.button_text}</a>
                <span className="absolute inset-0 bg-white/10 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              </motion.button> */}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-8"
            >
              <Social />
            </motion.div>
          </motion.div>

          {/* Desktop Photo */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, type: "spring" }}
            className="lg:w-1/2 relative"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-green-500 rounded-2xl opacity-20 blur-xl"></div>
              <div className="relative overflow-hidden rounded-xl shadow-2xl border-2 border-white/10">
                <Photo onPhoto={`${imageUrl}/${heroes.image}`} />
              </div>
              <motion.div 
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 1.2 }}
                className="absolute -bottom-6 -left-6 bg-gradient-to-br from-green-500 to-green-600 text-white p-4 rounded-xl shadow-xl"
              >
                <div className="text-center">
                  <div className="text-3xl font-bold">{heroes.happy_customers}</div>
                  <div className="text-sm">Happy Students</div>
                </div>
              </motion.div>
              
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -top-6 -right-6 bg-white/5 backdrop-blur-sm p-3 rounded-full border border-white/10 shadow-lg"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;